import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFilter, FiX, FiChevronDown, FiPackage } from 'react-icons/fi';
import api from '../utils/api';
import SkillCard, { type SkillData } from '../components/SkillCard';
import SearchBar from '../components/SearchBar';
import AgentBadge from '../components/AgentBadge';

const CATEGORIES = [
  'code-quality', 'testing', 'documentation', 'git', 'automation',
  'refactoring', 'security', 'api', 'database', 'devops',
  'frontend', 'backend', 'mobile', 'ai-ml', 'data',
  'architecture', 'performance', 'accessibility', 'debugging', 'deployment',
];

const AGENTS = [
  'claude-code', 'cursor', 'windsurf', 'codex', 'copilot',
  'aider', 'cline', 'roo-code', 'continue', 'bolt',
  'devin', 'replit-agent', 'lovable', 'v0', 'gemini-cli',
  'amp', 'opencode', 'kilo-code', 'zed', 'aide',
];

const SORT_OPTIONS = [
  { value: 'popular', label: 'Most Popular' },
  { value: 'recent', label: 'Recently Added' },
  { value: 'trending', label: 'Trending' },
  { value: 'name', label: 'Alphabetical' },
];

const PLACEHOLDER_SKILLS: SkillData[] = [
  { _id: '1', name: 'code-review', owner: 'strongly-ai', description: 'Comprehensive code review with security analysis, performance optimization, and best practices.', categories: ['code-quality', 'security'], compatibleAgents: ['claude-code', 'cursor', 'windsurf'], installCount: 12450, stars: 342, authorUsername: 'strongly-ai' },
  { _id: '2', name: 'git-workflow', owner: 'devtools', description: 'Advanced git workflow automation including branch management, PR creation, and conventions.', categories: ['git', 'automation'], compatibleAgents: ['claude-code', 'cursor', 'aider', 'copilot'], installCount: 8920, stars: 256, authorUsername: 'devtools' },
  { _id: '3', name: 'test-generator', owner: 'testlab', description: 'Auto-generate comprehensive test suites with edge cases, mocking strategies, and coverage optimization.', categories: ['testing', 'automation'], compatibleAgents: ['claude-code', 'cursor', 'windsurf', 'cline'], installCount: 7650, stars: 198, authorUsername: 'testlab' },
  { _id: '4', name: 'docs-writer', owner: 'docsmith', description: 'Generate and maintain documentation with API docs, README files, and inline comments.', categories: ['documentation'], compatibleAgents: ['claude-code', 'cursor', 'copilot'], installCount: 6340, stars: 167, authorUsername: 'docsmith' },
  { _id: '5', name: 'refactor-pro', owner: 'cleancode', description: 'Intelligent refactoring with design pattern suggestions, code smell detection, and architecture improvements.', categories: ['refactoring', 'architecture'], compatibleAgents: ['claude-code', 'cursor', 'aider'], installCount: 5120, stars: 145, authorUsername: 'cleancode' },
  { _id: '6', name: 'api-builder', owner: 'apicraft', description: 'Build REST and GraphQL APIs with validation, authentication, and OpenAPI documentation.', categories: ['api', 'backend'], compatibleAgents: ['claude-code', 'cursor', 'windsurf', 'devin'], installCount: 4890, stars: 132, authorUsername: 'apicraft' },
  { _id: '7', name: 'sql-optimizer', owner: 'dbpro', description: 'Analyze and optimize SQL queries for performance with index suggestions and query rewrites.', categories: ['database', 'performance'], compatibleAgents: ['claude-code', 'cursor'], installCount: 4210, stars: 118, authorUsername: 'dbpro' },
  { _id: '8', name: 'security-audit', owner: 'secteam', description: 'Automated security vulnerability scanning with OWASP Top 10 checks and remediation guidance.', categories: ['security'], compatibleAgents: ['claude-code', 'cursor', 'windsurf', 'copilot'], installCount: 3870, stars: 105, authorUsername: 'secteam' },
  { _id: '9', name: 'deploy-config', owner: 'opsmaster', description: 'Generate deployment configurations for Docker, Kubernetes, and cloud platforms.', categories: ['devops', 'deployment'], compatibleAgents: ['claude-code', 'cursor', 'devin'], installCount: 3450, stars: 94, authorUsername: 'opsmaster' },
];

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [skills, setSkills] = useState<SkillData[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [agent, setAgent] = useState(searchParams.get('agent') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'popular');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const fetchSkills = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, per_page: 12, sort };
      if (search) params.search = search;
      if (category) params.category = category;
      if (agent) params.agent = agent;

      const response = await api.get('/skills', { params });
      setSkills(response.data.skills || []);
      setTotalPages(response.data.pagination?.totalPages || response.data.totalPages || 1);
    } catch {
      // Use placeholder data filtered
      let filtered = [...PLACEHOLDER_SKILLS];
      if (search) {
        const q = search.toLowerCase();
        filtered = filtered.filter(s =>
          s.name.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q) ||
          (s.owner || '').toLowerCase().includes(q)
        );
      }
      if (category) {
        filtered = filtered.filter(s => s.categories?.includes(category));
      }
      if (agent) {
        filtered = filtered.filter(s => s.compatibleAgents?.includes(agent));
      }
      if (sort === 'name') filtered.sort((a, b) => a.name.localeCompare(b.name));
      if (sort === 'recent') filtered.reverse();
      setSkills(filtered);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [search, category, agent, sort, page]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  // Sync URL params
  useEffect(() => {
    const params: Record<string, string> = {};
    if (search) params.q = search;
    if (category) params.category = category;
    if (agent) params.agent = agent;
    if (sort !== 'popular') params.sort = sort;
    setSearchParams(params, { replace: true });
  }, [search, category, agent, sort, setSearchParams]);

  const clearFilters = () => {
    setSearch('');
    setCategory('');
    setAgent('');
    setSort('popular');
    setPage(1);
  };

  const hasActiveFilters = search || category || agent || sort !== 'popular';

  return (
    <div className="pt-24 pb-16 min-h-screen">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
            Browse <span className="gradient-text">Skills</span>
          </h1>
          <p className="text-slate-400">
            Explore the open directory of reusable skills for AI coding agents.
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="mb-6"
        >
          <SearchBar
            value={search}
            onChange={(val) => { setSearch(val); setPage(1); }}
          />
        </motion.div>

        {/* Filter Toggle + Sort */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex items-center justify-between mb-6"
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                showFilters
                  ? 'bg-brand-500/10 text-brand-400 border border-brand-500/30'
                  : 'glass text-slate-400 hover:text-white'
              }`}
            >
              <FiFilter size={14} />
              Filters
              {hasActiveFilters && (
                <span className="w-2 h-2 rounded-full bg-brand-500" />
              )}
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-xs text-slate-500 hover:text-white transition-colors flex items-center gap-1"
              >
                <FiX size={12} />
                Clear all
              </button>
            )}
          </div>

          {/* Sort Dropdown */}
          <div className="relative">
            <select
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1); }}
              className="appearance-none glass text-sm text-slate-300 px-4 py-2 pr-10 rounded-xl cursor-pointer
                         bg-transparent outline-none border border-white/5 hover:border-white/10 transition-colors"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-surface-50 text-white">
                  {opt.label}
                </option>
              ))}
            </select>
            <FiChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          </div>
        </motion.div>

        {/* Filters Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden mb-8"
            >
              <div className="glass-card p-6 space-y-6">
                {/* Categories */}
                <div>
                  <h4 className="text-sm font-medium text-slate-400 mb-3">Category</h4>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat}
                        onClick={() => { setCategory(category === cat ? '' : cat); setPage(1); }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          category === cat
                            ? 'bg-brand-500/15 text-brand-400 border border-brand-500/30'
                            : 'bg-white/5 text-slate-400 border border-white/5 hover:border-white/10 hover:text-white'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Compatible Agent */}
                <div>
                  <h4 className="text-sm font-medium text-slate-400 mb-3">Compatible Agent</h4>
                  <div className="flex flex-wrap gap-2">
                    {AGENTS.map((a) => (
                      <button
                        key={a}
                        onClick={() => { setAgent(agent === a ? '' : a); setPage(1); }}
                        className={`transition-all ${agent === a ? 'ring-2 ring-brand-500/50 rounded-full' : ''}`}
                      >
                        <AgentBadge agentName={a} size="md" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="glass-card p-6 h-52 shimmer rounded-2xl" />
            ))}
          </div>
        ) : skills.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {skills.map((skill, idx) => (
                <SkillCard key={skill._id} skill={skill} index={idx} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 rounded-lg text-sm glass text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                      page === i + 1
                        ? 'bg-brand-500/15 text-brand-400 border border-brand-500/30'
                        : 'glass text-slate-400 hover:text-white'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 rounded-lg text-sm glass text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          /* Empty State */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-24"
          >
            <div className="w-20 h-20 rounded-2xl bg-white/5 mx-auto mb-6 flex items-center justify-center">
              <FiPackage size={32} className="text-slate-600" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No skills found</h3>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Try adjusting your search or filters. Or be the first to publish a skill in this category!
            </p>
            <button
              onClick={clearFilters}
              className="btn-ghost text-sm px-6 py-2.5"
            >
              Clear Filters
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
